import type { DateTime, Duration } from "luxon"
import { useCallback, useState } from "react"

import { UnreachableCaseError } from "../../lib/lang"
import { useDateTime } from "../../modules/localization/useDateTime"

import { useInterval } from "./useInterval"

// Ported from room-display (src/modules/time/useClock.ts) so clock-driven UI ticks the same way everywhere.
type Options = Readonly<{
    resolution: "second" | "minute" | "day"
    offset?: Duration
}>

function getIntervalForResolution(resolution: Options["resolution"]) {
    switch (resolution) {
        case "second":
            return 300
        case "minute":
            return 1000
        case "day":
            return 10 * 1000
        default:
            throw new UnreachableCaseError(resolution)
    }
}

function getAdjustedClock(now: DateTime, offset: Duration | undefined) {
    return offset ? now.plus(offset) : now
}

// Polls faster than the resolution but only publishes a new value when the clock crosses a resolution
// boundary, so consumers re-render exactly when the displayed time changes.
export function useClock({ resolution, offset }: Options) {
    const dateTimeFactory = useDateTime()

    const [clock, setClock] = useState<DateTime>(() => getAdjustedClock(dateTimeFactory.now(), offset))

    const tick = useCallback(() => {
        setClock((prev) => {
            const adjustedClock = getAdjustedClock(dateTimeFactory.now(), offset)
            return adjustedClock.hasSame(prev, resolution) ? prev : adjustedClock
        })
    }, [resolution, offset, dateTimeFactory])

    useInterval(tick, getIntervalForResolution(resolution))

    return { clock }
}
